import { Button, CircularProgress } from '@mui/material'
import Box from '@mui/material/Box'
import { useEffect, useState } from 'react'
import DataTable from '../../Components/Table/DataTable'
import { Column, ColumnSort, ColumnType, type RowData } from '../../Components/Table/DataTable/DataTable.model'
import { ReferralSearch, type ReferralSearchCriteria } from './ReferralSearch'
import { FormatDateValue } from '../../core/Utilities'
import { type PaginationProps, usePagination, type SearchCriteria } from '../../hooks/use-pagination'
import { SentSearch, type SentSearchCriteria } from './SentSearch'
import { ReferralDetail } from './ReferralDetail'
import { AddChildModal } from './AddChildModal/AddChildModal'
import { ErrorProvider } from '../../ContextProviders/ErrorProvider'
import { toast } from 'react-toastify'
import { useParams } from 'react-router-dom'
import S3Image from '../../Components/S3Image'
import { sendGet } from '../../hooks/use-fetch'
import { type lookup } from '../../core/types'

const nameColumn = new Column('childName', 'Name', ColumnType.CUSTOM)
nameColumn.customCellGenerator = row => {
  if (row.S3Key != null) {
    return <div>
      <S3Image
        imageKey={row.s3Key}
        alt='child image'
        style={{ maxWidth: '100px' }}
      />
      <span>{row.childName}</span>
    </div>
  } else {
    return <div>{row.childName}</div>
  }
}

const age1Column = new Column('age', 'Age')
const age2Column = new Column('ageOn91', 'Age on 9/1')

const dobColumn = new Column('dateOfBirth', 'DOB', ColumnType.CUSTOM)
dobColumn.customCellGenerator = row => {
  return <div>{FormatDateValue(row.dateOfBirth)} </div>
}

const genderColumn = new Column('gender', 'Gender')
const schoolYearColumn = new Column('schoolYear', 'School Year')
const schoolYearIdColumn = new Column('schoolYearId', 'School Year')
schoolYearIdColumn.visibility = false
const residenceColumn = new Column('residence', 'Residence')

const povertyPercentColumn = new Column('povertyPercent', 'Poverty %', ColumnType.CUSTOM)
povertyPercentColumn.customCellGenerator = row => {
  return <div>{row.povertyPercent == null ? '' : `${String(row.povertyPercent)}%`} </div>
}
const facilityIDColumn = new Column('requestedFacilityId', 'Facility ID')
facilityIDColumn.visibility = false
const facilityColumn = new Column('requestedFacility', 'Facility')
const programColumn = new Column('programName', 'Program', ColumnType.CUSTOM)
programColumn.customCellGenerator = row => {
  return <div>{row.programName != null ? row.programName : 'Program Missing'} </div>
}
const viewedColumn = new Column('beenViewed', 'Been Viewed')
viewedColumn.visibility = false

const recentActionColumn = new Column('actionInLast30Days', 'Recent Action')
recentActionColumn.visibility = false
const dateColumn = new Column('actionDate', 'Referred On', ColumnType.CUSTOM)
dateColumn.customCellGenerator = row => {
  return <>{FormatDateValue(row.actionDate)}</>
}

const columns = [nameColumn, dateColumn, age1Column, age2Column, dobColumn, schoolYearColumn, genderColumn,
  residenceColumn, facilityColumn, povertyPercentColumn, programColumn, viewedColumn, recentActionColumn, schoolYearIdColumn, facilityIDColumn]

export enum ReferralFilterType {
  Direct,
  Sent
}

export interface ReferralListProps {
  serviceID: string | undefined
  serviceProviderId: number | undefined
  loading: boolean
  filterType: ReferralFilterType
  rows: RowData[]
  showAddButton?: boolean | true
  onRefresh: () => void
}

export function ReferralList (props: ReferralListProps): JSX.Element {
  const [directSearchData, setDirectSearchData] = useState<ReferralSearchCriteria>({
    schoolYearId: -1,
    childName: '',
    facility: '',
    serviceID: Number(props.serviceID),
    serviceProviderId: Number(props.serviceProviderId)
  })
  const [schoolYearOptions, setSchoolYearOptions] = useState<lookup[]>([])
  const [sentSearchData, setSendSearchData] = useState<SentSearchCriteria>({ schoolYearId: -1, actionInLast30Days: false })
  const [selectedReferralID, setSelectedReferralID] = useState<number | null>(null)
  const [manualReferEnabled, setManualReferEnabled] = useState<boolean>(false)
  const [selectedReferralChildId, setSelectedReferralChildId] = useState<number>(0)

  const [open, setOpen] = useState(false)
  const [showAddChild, setShowAddChild] = useState(false)
  const { serviceId } = useParams()

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      const { response: rsp2 } = await sendGet('/ReferralSearch/DateRangeFilterList', {})
      if (rsp2 !== undefined) {
        rsp2.unshift({ id: -1, name: 'All' })
        setSchoolYearOptions(rsp2)
      }
    }
    void fetchData()
  }, [])

  const rowClick = (event: React.MouseEvent<HTMLTableRowElement, MouseEvent>, row: RowData): void => {
    setSelectedReferralID(row.id)
    setSelectedReferralChildId(row.childID)
    setManualReferEnabled(row.selectProviderOnReferralDecline)
    setOpen(true)
  }

  const paginationProps: PaginationProps<RowData> = {
    rows: props.rows,
    initialSize: 5,
    pageSizeSteps: [5, 10]
  }
  const pagination = usePagination(paginationProps)

  const handleDirectSearch = (newdata: ReferralSearchCriteria): void => {
    setDirectSearchData(newdata)
    const fieldArray: SearchCriteria[] = []
    if (newdata.childName !== '') {
      fieldArray.push({
        field: 'childName',
        value: newdata.childName
      })
    }
    if (newdata.schoolYearId !== -1) {
      fieldArray.push({
        field: 'schoolYearId',
        value: newdata.schoolYearId.toString()
      })
    }
    if (newdata.facility !== '' && newdata.facility !== 'showAll') {
      fieldArray.push({
        field: 'requestedFacilityId',
        value: newdata.facility.replaceAll(' ', ''),
        exact: true
      })
    }
    pagination.setSearchFields(fieldArray)
  }

  const handleSentSearch = (newdata: SentSearchCriteria): void => {
    setSendSearchData(newdata)
    const fieldArray: SearchCriteria[] = []
    if (newdata.schoolYearId !== -1) {
      fieldArray.push({
        field: 'schoolYearId',
        value: newdata.schoolYearId.toString()
      })
    }
    if (newdata.actionInLast30Days) {
      fieldArray.push({
        field: 'actionInLast30Days',
        value: 'true'
      })
    }
    pagination.setSearchFields(fieldArray)
  }

  if (props.loading) return <CircularProgress />
  const addButton = <Button data-testid="addReferralButton" variant='contained' color='secondary' onClick={() => { setShowAddChild(true) }}>Add Child Referral</Button>

  return (<>
    {props.filterType === ReferralFilterType.Direct && (props.showAddButton ?? true) && <ReferralSearch current={directSearchData} onChange={handleDirectSearch} addButton={addButton} schoolYearOptions={schoolYearOptions} />}
    {props.filterType === ReferralFilterType.Direct && !(props.showAddButton ?? true) && <ReferralSearch current={directSearchData} onChange={handleDirectSearch} schoolYearOptions={schoolYearOptions} />}
    {props.filterType === ReferralFilterType.Sent && <SentSearch current={sentSearchData} onChange={handleSentSearch} schoolYearOptions={schoolYearOptions} />}
    <div>
      <Box sx={{ width: '100%', padding: '30px 20px', borderRadius: '16px', background: '#fff' }}>
        <DataTable
          page={pagination.page}
          name='referralTable'
          data-testid='referralTable'
          columns={columns}
          rows={pagination.internalRows}
          totalRecords={pagination.recordCount}
          loading={props.loading}
          onSortChange={(col, sorts) => {
            pagination.onSortChange(col, sorts[0].order)
          }}
          onPageChange={pagination.handleChangePage}
          onRowsPerPageChange={pagination.handleChangeRowsPerPage}
          onRowClick={rowClick}
          initialColumnSorts={[new ColumnSort('childName')]}
          hasCheckbox={false}
        />
      </Box>

      <AddChildModal
        serviceID={Number(serviceId)}
        endpoint='ReferralEdit'
        serviceProviderEndpoint='ServiceProviderDetails'
        open={showAddChild}
        onClose={() => { setShowAddChild(false) }}
        onSubmit={(childID: number, referralID: number) => {
          if (childID === 0 && referralID === 0) {
            toast.success('Transfer request submitted.')
          } else if (referralID > 0) {
            toast.success('A referral was created and sent to the provider.')
          } else {
            toast.error('An error occurred while creating the referral.')
          }

          setShowAddChild(false)
          props.onRefresh()
        }}
      />

      {open && selectedReferralID !== null &&
        <ErrorProvider>
          <ReferralDetail
            onClose={() => { setOpen(false) }}
            onRefresh={props.onRefresh}
            open={open}
            referralID={selectedReferralID}
            manualReferEnabled={manualReferEnabled}
            childID={selectedReferralChildId}
            serviceID={Number(serviceId)}
            hideAccept={props.filterType === ReferralFilterType.Sent}
            hideDeny={props.filterType === ReferralFilterType.Sent}
            allowWaitlist={false}
          />
        </ErrorProvider>
      }
    </div>
  </>)
}
